import React, { useMemo } from 'react';
import { motion } from 'motion/react';
import { Users, CheckSquare, Clock, ArrowRight, Phone, MessageSquare } from 'lucide-react';
import WhatsAppIcon from '../components/icons/WhatsAppIcon';
import { Lead } from '../types/lead';
import { Task } from '../types/task';
import { AppTab } from '../components/Layout/AppShell';
import PageHeader from '../components/Layout/PageHeader';
import { useAuth } from '../contexts/AuthContext';

interface DashboardPageProps {
  leads: Lead[];
  tasks: Task[];
  onTabChange: (tab: AppTab) => void;
  onLeadClick: (lead: Lead) => void;
  userName: string;
}

const DashboardPage: React.FC<DashboardPageProps> = ({
  leads,
  tasks,
  onTabChange,
  onLeadClick,
  userName,
}) => {
  const { isKunde } = useAuth();
  const activeTasks = useMemo(() => tasks.filter(t => !t.completed), [tasks]);
  const appointments = useMemo(() => tasks.filter(t => t.type === 'appointment' && !t.completed), [tasks]);
  const newLeads = useMemo(() => leads.filter(l => l.status === 'neu'), [leads]);

  // Letzte Aktivität: nur neue Leads, sortiert nach Eingang
  const recentNewLeads = useMemo(() =>
    [...newLeads]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 10),
    [newLeads]
  );

  const upcomingTasks = useMemo(() =>
    activeTasks
      .filter(t => t.due_date)
      .sort((a, b) => new Date(a.due_date!).getTime() - new Date(b.due_date!).getTime())
      .slice(0, 5),
    [activeTasks]
  );

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const formatRelative = (iso: string) => {
    const diff = Date.now() - new Date(iso).getTime();
    const h = Math.floor(diff / 3600000);
    const d = Math.floor(h / 24);
    if (h < 1) return 'Gerade eben';
    if (h < 24) return `vor ${h}h`;
    if (d < 7) return `vor ${d}d`;
    return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  };

  const handleCall = (e: React.MouseEvent, phone: string) => {
    e.stopPropagation();
    window.location.href = `tel:${phone.replace(/\s/g, '')}`;
  };

  const handleWhatsApp = (e: React.MouseEvent, phone: string) => {
    e.stopPropagation();
    const clean = phone.replace(/\D/g, '');
    window.open(`https://wa.me/${clean}`, '_blank');
  };

  const handleSMS = (e: React.MouseEvent, phone: string) => {
    e.stopPropagation();
    window.location.href = `sms:${phone.replace(/\s/g, '')}`;
  };

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle={`Willkommen zurück, ${userName}`}
      />

      <div className="px-4 md:px-8 py-6 space-y-6">

        {/* Stat Cards — kompakt auf Mobile, dezent */}
        <div className={`grid gap-3 ${isKunde() ? 'grid-cols-1' : 'grid-cols-3'}`}>
          {/* Neue Leads */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0 }}
            className="bg-white border border-zinc-200 rounded-xl p-3 md:p-5 flex flex-col md:flex-row md:items-start md:gap-4"
          >
            <div className="bg-zinc-900 rounded-lg p-2 md:p-2.5 text-white self-start mb-2 md:mb-0 flex-shrink-0">
              <Users className="w-4 h-4 md:w-5 md:h-5" />
            </div>
            <div>
              <p className="text-[10px] md:text-xs font-semibold text-zinc-400 uppercase tracking-widest leading-tight">Neue Anfragen</p>
              <p className="text-2xl md:text-3xl font-black text-zinc-900">{newLeads.length}</p>
              <p className="text-[10px] md:text-xs text-zinc-400 hidden md:block">Unbearbeitet</p>
            </div>
          </motion.div>

          {/* Termine */}
          {!isKunde() && <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-white border border-zinc-200 rounded-xl p-3 md:p-5 flex flex-col md:flex-row md:items-start md:gap-4"
          >
            <div className="bg-violet-600 rounded-lg p-2 md:p-2.5 text-white self-start mb-2 md:mb-0 flex-shrink-0">
              <Clock className="w-4 h-4 md:w-5 md:h-5" />
            </div>
            <div>
              <p className="text-[10px] md:text-xs font-semibold text-zinc-400 uppercase tracking-widest leading-tight">Termine</p>
              <p className="text-2xl md:text-3xl font-black text-zinc-900">{appointments.length}</p>
              <p className="text-[10px] md:text-xs text-zinc-400 hidden md:block">Ausstehend</p>
            </div>
          </motion.div>}

          {/* Tasks */}
          {!isKunde() && <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white border border-zinc-200 rounded-xl p-3 md:p-5 flex flex-col md:flex-row md:items-start md:gap-4"
          >
            <div className="bg-blue-600 rounded-lg p-2 md:p-2.5 text-white self-start mb-2 md:mb-0 flex-shrink-0">
              <CheckSquare className="w-4 h-4 md:w-5 md:h-5" />
            </div>
            <div>
              <p className="text-[10px] md:text-xs font-semibold text-zinc-400 uppercase tracking-widest leading-tight">Tasks</p>
              <p className="text-2xl md:text-3xl font-black text-zinc-900">{activeTasks.length}</p>
              <p className="text-[10px] md:text-xs text-zinc-400 hidden md:block">Offen</p>
            </div>
          </motion.div>}
        </div>

        {/* Main content */}
        <div className={`grid gap-6 ${isKunde() ? 'grid-cols-1' : 'md:grid-cols-2'}`}>
          {/* Neue Eingänge */}
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
              <div>
                <h3 className="font-bold text-zinc-900 uppercase tracking-widest text-xs">Neue Eingänge</h3>
                <p className="text-[10px] text-zinc-400 mt-0.5">{newLeads.length} unbearbeitete Anfragen</p>
              </div>
              <button
                onClick={() => onTabChange('leads')}
                className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 transition-colors font-medium cursor-pointer"
              >
                Alle <ArrowRight className="w-3 h-3" />
              </button>
            </div>
            <div className="divide-y divide-zinc-50">
              {recentNewLeads.length === 0 ? (
                <p className="text-zinc-400 text-sm text-center py-8">Keine neuen Anfragen</p>
              ) : (
                recentNewLeads.map((lead) => (
                  <div key={lead.id} onClick={() => onLeadClick(lead)} className="group flex items-center justify-between px-5 py-3 hover:bg-zinc-50 transition-colors cursor-pointer">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-zinc-900 truncate">{lead.name}</p>
                      <p className="text-xs text-zinc-400 truncate">{lead.company || lead.email}</p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-3">
                      {lead.phone && (
                        <>
                          <button
                            onClick={e => handleCall(e, lead.phone)}
                            className="p-1.5 rounded-lg hover:bg-emerald-50 text-zinc-400 hover:text-emerald-600 transition-colors"
                            title="Anrufen"
                          >
                            <Phone className="w-4 h-4" />
                          </button>
                          <button
                            onClick={e => handleWhatsApp(e, lead.phone)}
                            className="p-1.5 rounded-lg hover:bg-green-50 text-zinc-400 hover:text-[#25D366] transition-colors"
                            title="WhatsApp"
                          >
                            <WhatsAppIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={e => handleSMS(e, lead.phone)}
                            className="p-1.5 rounded-lg hover:bg-blue-50 text-zinc-400 hover:text-blue-500 transition-colors"
                            title="SMS"
                          >
                            <MessageSquare className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      <span className="text-xs text-zinc-400 ml-1">{formatRelative(lead.created_at)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Anstehende Aufgaben */}
          {!isKunde() && <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
              <div>
                <h3 className="font-bold text-zinc-900 uppercase tracking-widest text-xs">Anstehende Aufgaben</h3>
                <p className="text-[10px] text-zinc-400 mt-0.5">{activeTasks.length} offen</p>
              </div>
              <button
                onClick={() => onTabChange('tasks')}
                className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 transition-colors font-medium cursor-pointer"
              >
                Alle <ArrowRight className="w-3 h-3" />
              </button>
            </div>
            <div className="divide-y divide-zinc-50">
              {upcomingTasks.length === 0 ? (
                <p className="text-zinc-400 text-sm text-center py-8">Keine Aufgaben ausstehend</p>
              ) : (
                upcomingTasks.map((task) => (
                  <div key={task.id} className="flex items-center gap-3 px-5 py-3 hover:bg-zinc-50 transition-colors">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${task.is_important ? 'bg-amber-400' : 'bg-zinc-300'}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-zinc-900 truncate">{task.title}</p>
                      {task.lead_name && (
                        <p className="text-xs text-zinc-400 truncate">{task.lead_name}</p>
                      )}
                    </div>
                    <span className="text-xs text-zinc-400 flex-shrink-0">
                      {task.due_date ? formatDate(task.due_date) : '–'}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
